import { useEffect, useState, useCallback } from "react";

const VISITOR_ID_KEY = "alabama-visitor-id";
const MEMORY_KEY = "alabama-site-memory";

export interface SiteMemory {
  visitorId: string;
  hasVisited: boolean;
  lastPage: string;
  chatOpen: boolean;
  chatHistory: Array<{ role: "user" | "assistant"; content: string }>;
  createdAt: string;
  updatedAt: string;
}

const getDefaultMemory = (): SiteMemory => ({
  visitorId: "",
  hasVisited: false,
  lastPage: "/",
  chatOpen: false,
  chatHistory: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// Initialize visitor ID once
const getOrCreateVisitorId = (): string => {
  if (typeof window === "undefined") return "";
  
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  return visitorId;
};

// Load memory from localStorage
const loadMemory = (): SiteMemory => {
  if (typeof window === "undefined") return getDefaultMemory();
  
  try {
    const stored = localStorage.getItem(MEMORY_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as SiteMemory;
      return {
        ...getDefaultMemory(),
        ...parsed,
        visitorId: getOrCreateVisitorId(),
      };
    }
  } catch {
    // Ignore parse errors
  }
  
  return {
    ...getDefaultMemory(),
    visitorId: getOrCreateVisitorId(),
  };
};

// Save memory to localStorage
const saveMemory = (memory: SiteMemory) => {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.setItem(MEMORY_KEY, JSON.stringify({
      ...memory,
      updatedAt: new Date().toISOString(),
    }));
  } catch {
    // Ignore storage errors
  }
};

export function useSiteMemory() {
  const [memory, setMemory] = useState<SiteMemory>(loadMemory);

  // Mark as visited on first load
  useEffect(() => {
    if (!memory.hasVisited) {
      setMemory(prev => ({ ...prev, hasVisited: true }));
    }
  }, []);

  // Persist memory whenever it changes
  useEffect(() => {
    saveMemory(memory);
  }, [memory]);

  // Update last page on route change
  const updateLastPage = useCallback((path: string) => {
    setMemory(prev => ({ ...prev, lastPage: path }));
  }, []);

  // Chat state management
  const setChatOpen = useCallback((open: boolean) => {
    setMemory(prev => ({ ...prev, chatOpen: open }));
  }, []);

  const addChatMessage = useCallback((role: "user" | "assistant", content: string) => {
    setMemory(prev => ({
      ...prev,
      chatHistory: [...prev.chatHistory, { role, content }],
    }));
  }, []);

  const clearChatHistory = useCallback(() => {
    setMemory(prev => ({ ...prev, chatHistory: [] }));
  }, []);

  return {
    memory,
    updateLastPage,
    setChatOpen,
    addChatMessage,
    clearChatHistory,
  };
}

// Export utility to get visitor ID without hook
export const getVisitorId = getOrCreateVisitorId;
